// SamplingEvent.js
// This is the main class to handle the sampling event data and save it in one location to be used throughout the SamplingEvent module

import { SamplingGroup } from "./SamplingGroup";
import { WaterCalibrationLog } from "./WaterCalibrationLog";
import { JsaV2 } from "../Jsa/JsaV2";
import { DailySummary } from "./DailySummary";
import { Wells } from "./Wells";
import { EmployeeFieldNotes } from "./FieldNotes";

export class SamplingEvent {
  constructor(data) {
    this.id = data.id;
    this.eventName = data.event_name;
    this.startDate = data.start_date;
    this.endDate = data.end_date;
    this.createdBy = data.created_by;
    this.createdByName = data.created_by_name;
    this.teamLead = data.team_lead;
    this.teamLeadName = data.team_lead_name;
    this.wellSite = data.well_site;
    this.projectNumber = data.project_number;
    this.phaseId = data.phase_id;
    this.taskId = data.task_id;
    this.comments = data.comments;
    this.completed = data.completed;
    this.completedDate = data.completed_date;
    this.completedTime = data.completed_time;
    this.completedBy = data.completed_by;

    // Sampling groups with nested field notes
    this.samplingGroups = data.sampling_groups
      ? data.sampling_groups.map(
          (samplingGroup) => new SamplingGroup(samplingGroup)
        )
      : [];

    // Sampling event wells (previously sampling_wells)
    this.samplingEventWells = data.sampling_event_wells
      ? data.sampling_event_wells.map((samplingWell) => new Wells(samplingWell))
      : [];

    // Daily summaries
    this.dailySummaries = data.daily_summaries
      ? data.daily_summaries.map(
          (dailySummary) => new DailySummary(dailySummary)
        )
      : [];

    // Water calibration log
    this.waterCalibrationLogs =
      data.water_calibration_log && data.water_calibration_log.length > 0
        ? data.water_calibration_log.map((log) => new WaterCalibrationLog(log))
        : [];

    // Field notes
    this.fieldNotes = data.field_notes
      ? data.field_notes.map((fieldNote) => new EmployeeFieldNotes(fieldNote))
      : [];

    // JSA data, which will be a list of JSAs
    this.jsa = data.jsa ? data.jsa.map((jsa) => new JsaV2(jsa)) : [new JsaV2()];

    // Specific for the JSA component section
    this.activeComponentTabs = {
      JSA_TAB: {
        ACTIVE_TAB: "All",
        SIGNATURE_TAB: "Employee Signatures",
      },
    };

    // JSA updates to monitor in the JSA tab
    this.jsaUpdatesToMonitor = {
      action: null,
      jsaId: null,
      updatedFields: null,
    };
  }

  // Helper function to format dates for the back end
  _formatDate(date) {
    if (!date) return null;
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  setCompleted(completed, completedDate, completedTime, completedBy) {
    this.completed = completed;
    this.completedDate = this._formatDate(completedDate);
    this.completedTime = completedTime;
    this.completedBy = completedBy;
  }

  fromExistingSamplingEvent(allProjects) {
    const projects = Array.isArray(allProjects) ? allProjects : [];
    const project = this.projectNumber
      ? projects.find((p) => p.project_number === this.projectNumber)
      : null;

    this.projectLabel = project?.label || "";
    this.projectName = project?.project_name || "";

    const rawPhases = Array.isArray(project?.phase_list)
      ? project.phase_list
      : [];
    this.availablePhases = rawPhases.map((phase) => ({
      value: phase.phase_id,
      label: phase.label,
      task_list: Array.isArray(phase.task_list) ? phase.task_list : [],
    }));

    const activePhase = this.phaseId
      ? this.availablePhases.find((phase) => phase.value === this.phaseId)
      : null;
    this.phaseName = activePhase?.label || "";

    const rawTasks = Array.isArray(activePhase?.task_list)
      ? activePhase.task_list
      : [];
    this.availableTasks = rawTasks.map((task) => ({
      value: task.task_id,
      label: task.label,
    }));

    const activeTask = this.taskId
      ? this.availableTasks.find((task) => task.value === this.taskId)
      : null;
    this.taskName = activeTask?.label || "";
  }

  addNewBlankDailySummary() {
    this.dailySummaries.push(new DailySummary({}));
  }

  addNewBlankSamplingGroup() {
    this.samplingGroups.push(new SamplingGroup({}));
  }

  removeSamplingGroup(samplingGroupId) {
    this.samplingGroups = this.samplingGroups.filter(
      (samplingGroup) => samplingGroup.id !== samplingGroupId
    );
  }

  addNewWaterCalibrationLog(date, time, user) {
    const newLog = new WaterCalibrationLog({
      calibration_date: date,
      calibration_time: time,
      instrument: "",
      field_parameter: "",
      standard_used: "",
      verification: "",
      units: "",
      pass_fail: "",
      employee_id: user.employee_id,
      employee_name: user.name,
      created_at: new Date(),
      updated_at: new Date(),
      sampling_event_id: this.id,
    });
    this.waterCalibrationLogs.push(newLog);
    return newLog;
  }

  // Sorting the water calibration logs, helper function to use on patch updates
  sortWaterCalibrationLogs() {
    if (!this.waterCalibrationLogs || this.waterCalibrationLogs.length === 0) {
      return [];
    }
    this.waterCalibrationLogs.sort((a, b) => {
      const dateA = a.calibrationDate
        ? new Date(a.calibrationDate)
        : new Date(0);
      const dateB = b.calibrationDate
        ? new Date(b.calibrationDate)
        : new Date(0);
      if (dateA > dateB) return -1;
      if (dateA < dateB) return 1;

      // If dates are the same, sort by time
      if (a.calibrationTime && b.calibrationTime) {
        return a.calibrationTime.localeCompare(b.calibrationTime);
      }
      return 0;
    });
    return this.waterCalibrationLogs;
  }

  removeWaterCalibrationLog(waterCalibrationRowId) {
    this.waterCalibrationLogs = this.waterCalibrationLogs.filter(
      (log) => log.id !== waterCalibrationRowId
    );
  }

  // Add new sampling event well
  addNewBlankSamplingEventWell() {
    this.samplingEventWells.push(new Wells({}));
  }

  // Remove sampling event well
  removeSamplingEventWell(wellId) {
    this.samplingEventWells = this.samplingEventWells.filter(
      (well) => well.id !== wellId
    );
  }

  // Add new blank field note
  addNewBlankFieldNote() {
    const newFieldNote = new EmployeeFieldNotes({});
    this.fieldNotes.push(newFieldNote);
    return newFieldNote;
  }

  // Add new JSA from copy JSA
  addNewJsa(data) {
    this.jsa.push(new JsaV2(data));
  }

  // Add a method to remove a JSA by its ID
  removeJsa(jsaId) {
    this.jsa = this.jsa.filter((jsa) => jsa.jsaBasicInfo.id !== jsaId);
  }

  // Updating the JSA updates to monitor
  updateJsaUpdatesToMonitor(action, jsaId, updatedFields) {
    this.jsaUpdatesToMonitor.action = action;
    this.jsaUpdatesToMonitor.jsaId = jsaId;
    this.jsaUpdatesToMonitor.updatedFields = updatedFields;
  }

  toPatch(fields) {
    const fieldMap = {
      completed: "completed",
      completedDate: "completed_date",
      completedTime: "completed_time",
      completedBy: "completed_by",
    };
    const patch = {
      patch_type: "main_sampling_event",
    };
    for (const [key, value] of Object.entries(fields)) {
      const dbField = fieldMap[key];
      if (dbField) {
        patch[dbField] = value;
      }
    }
    return patch;
  }

  /**
   * Handles all JSA-related patches to keep the main applyPatch cleaner.
   * @param {object} patchData The patch data from the server.
   * @returns {SamplingEvent} The modified `this` instance for chaining.
   */
  applyJsaPatch(patchData) {
    const { patch_type, patch } = patchData;

    switch (patch_type) {
      case "jsa_update": {
        const { id: jsaId } = patch;
        const jsaIndex = this.jsa.findIndex(
          (jsa) => jsa.jsaBasicInfo.id === jsaId
        );
        if (jsaIndex > -1) {
          // Delegate to the JSA instance's applyPatch method.
          this.jsa[jsaIndex] = this.jsa[jsaIndex].applyPatch(patch);
        }
        break;
      }
      case "delete_jsa": {
        const { id: jsaId } = patch;
        this.jsa = this.jsa.filter((jsa) => jsa.jsaBasicInfo.id !== jsaId);
        break;
      }
      case "copy_jsa": {
        const { id: jsaId, copied_jsa } = patch;
        const exists = this.jsa.some((jsa) => jsa.jsaBasicInfo.id === jsaId);
        if (!exists) {
          this.jsa.push(new JsaV2(copied_jsa));
        }
        break;
      }
    }
    return this;
  }

  /**
   * Serializes the class instance to a JSON object for cloning or sending to the server.
   * @returns {object} A plain JavaScript object representing the event.
   */
  toJson() {
    return {
      id: this.id,
      event_name: this.eventName,
      start_date: this.startDate,
      end_date: this.endDate,
      created_by: this.createdBy,
      created_by_name: this.createdByName,
      team_lead: this.teamLead,
      team_lead_name: this.teamLeadName,
      well_site: this.wellSite,
      project_number: this.projectNumber,
      phase_id: this.phaseId,
      task_id: this.taskId,
      comments: this.comments,
      completed: this.completed,
      completed_date: this.completedDate,
      completed_time: this.completedTime,
      completed_by: this.completedBy,
      sampling_groups: this.samplingGroups.map((group) => group.toJson()),
      sampling_event_wells: this.samplingEventWells.map((well) =>
        well.toJson()
      ),
      daily_summaries: this.dailySummaries.map((summary) =>
        typeof summary?.toJson === "function"
          ? summary.toJson()
          : new DailySummary(summary).toJson()
      ),
      water_calibration_log: this.waterCalibrationLog.map((log) =>
        log.toJson()
      ),
      field_notes: this.fieldNotes.map((note) => note.toJson()),
      jsa: this.jsa.map((jsa) => jsa.toJson()),
    };
  }
}
